import { ILogger } from '../../logger';
import { PLAYBACK_RATES } from './constants';
import { IFeatureFlags } from '@audacy-clients/core/atoms/config/settings';

export type TPlaybackRate = typeof PLAYBACK_RATES[number];
export interface IPlayerMetadata {
  id?: string;
  stationId?: string;
  artist?: string;
  companionAdIframeUrl?: string;
  duration?: number;
  image?: string;
  isSkippable?: boolean;
  skips?: number;
  songOrShow?: string;
  start?: number;
  station?: string; // should take precedence over content object station name
  stop?: number;
  trackStart?: number;
  playId?: string;
  canPause?: boolean;
  isLive?: boolean;
  segmentStartTime?: number;
  contentType?: string;
  trackId?: string;
  datumTime?: number;
  isRewindable?: boolean | string;
  // NOTE - [CCS-2589] - VOD: These are for VOD streams
  playlistTailStartTime?: number;
  playlistNextStartTime?: number;
  liveTime?: number;
  playlistDuration?: number;
  continuity?: number;
  shfId?: string;
  isAudioPreroll?: boolean;
}

export type TPlayerEvent = { raw?: unknown } & (
  | {
      type:
        | 'continuePlay'
        | 'loading'
        | 'loaded'
        | 'playing'
        | 'paused'
        | 'destroyed'
        | 'error'
        | 'stats'
        | 'playbackFailed'
    }
  | {
      type: 'ended';
      shouldDestroy?: boolean;
      playlistNextStartTime?: number;
    }
  | {
      type: 'update';
      time: number;
      duration?: number;
    }
  | ({
      type: 'metadata';
    } & IPlayerMetadata)
  | {
      type: 'continuePlaying';
      playlistNextStartTime: number;
      playLive?: boolean;
    }
  | {
      type: 'superHiFiResumePoint';
      continuity: number;
      segmentTime: number;
  }
);

// "Live" | "Live-Linear" | "DVR" | "Catchup" | "VOD"
export enum EConvivaContentType {
  Live = 'Live',
  VOD = 'VOD',
}

export type TContentInfoStatic = {
  assetName: string; // should follow a format of ""[contentId] content title"
  viewerId: string; // AUDToken
  appVersion: string // app version - should pull from the env like it does in other places
  categoryType: string // format of "entityType:entitySubtype"
  contentType: EConvivaContentType;
  contentId: string // the content id for the item a user clicked play on
}

export type TContentInfoDynamic = {
  isLive: boolean;
  streamUrl: string;
}

export type TPlayerEventListener = (event: TPlayerEvent) => void;
export interface IPlayer {
  getCurrentTime(): number; // temporary: rename to getPosition() once streamers are migrated
  pause(): void;
  load({ url, isPodcast, isSuperHifi, isAudioPreroll }: IPlayerLoad): void;
  play(): void;
  playSuperHiFi(): void;
  stopSuperHiFi(): void;
  playWithOffset(offset?: number): void;
  setPosition(position: number): void;
  getMuted(): boolean;
  setMuted(isMuted: boolean): void;
  setDefaultRate(speed: TPlaybackRate): void;
  setRate(speed: TPlaybackRate): void;
  getRate(): TPlaybackRate;
  setVolume(volume: number): void;
  getVolume(): number;
  skip(secs: number): void;
  stop(): void;
  addEventListener(listener: TPlayerEventListener): void;
  removeEventListener(listener: TPlayerEventListener): void;
  silentAudioHack?(): void;
  playPrerollInProgress(): void;
  // NOTE: CCS-1104 (VOD speed controls): Remove after full feature flag rollout
  vodFeatureEnabled?: boolean;
  allowSkips?: boolean;
  isAudioPreroll?: boolean;
  reportPlaybackRequested?(contentInfoStatic: TContentInfoStatic): void;
  reportPlaybackEnded?(): void;
  setContentInfo?(contentInfoDynamic: TContentInfoDynamic): void;
}

export interface IHtmlPlayer extends IPlayer {
  playerElement: HTMLMediaElement;
}

export interface IPlayerOptions {
  logger?: ILogger;
  featureFlags?: IFeatureFlags;
}

export interface ISuperHifiCurrent {
  type?: string;
  id?: string;
  title?: string;
  artist?: string;
  album?: string;
  image?: string;
  isrc?: string;
  duration_ms?: string;
  start_ms?: string;
}

export interface IPlayerLoad {
  url: string;
  isPodcast?: boolean;
  isSuperHifi: boolean;
  isAudioPreroll: boolean;
  liveContentUrl: string;
  companionAd?: string;
  autoplay?: boolean;
}
